/**
 * backendSync.js — Sincroniza datos de formularios con el backend GCP.
 * Solo se invoca al completar un formulario (submit), no en cada cambio de campo.
 */
import {
  buildSignosFromForm,
  buildCondicionesArray,
  buildF2Firestore,
  buildF3Firestore,
  buildF4Firestore,
  buildF5Firestore,
  translateFormKeys,
} from "@/data/loinc";
import { getAutoDetectedConditions, calculateIMC } from "@/pages/FormF1/helpers";
import { getCoreServicesConfig } from "@/services/coreServicesClient";

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
  ? ""
  : RAW_BACKEND_URL;
const GCP_PROXY_URL = `${BACKEND_URL}/api/gcp`;

function getGcpHeaders() {
  try {
    const config = getCoreServicesConfig();
    const targetUrl = config?.apiUrl || config?.url;
    return {
      "Content-Type": "application/json",
      ...(targetUrl ? { "x-core-target-url": targetUrl } : {}),
      ...(config?.apiKey ? { "x-core-api-key": config.apiKey } : {})
    };
  } catch {
    return { "Content-Type": "application/json" };
  }
}

// ── Sync Log (persisted in localStorage) ──────────────────
const SYNC_LOG_KEY = "we360_sync_log";
const MAX_LOG_ENTRIES = 50;

function getSyncLog() {
  try {
    return JSON.parse(localStorage.getItem(SYNC_LOG_KEY) || "[]");
  } catch { return []; }
}

function addSyncLogEntry(entry) {
  const log = getSyncLog();
  log.unshift({ ...entry, timestamp: new Date().toISOString() });
  if (log.length > MAX_LOG_ENTRIES) log.length = MAX_LOG_ENTRIES;
  localStorage.setItem(SYNC_LOG_KEY, JSON.stringify(log));
}

export { getSyncLog };

/**
 * Build the F1 payload - reads from Firebase and transforms to backend format
 * @param {string} userId - Firebase UID to fetch fresh FHIR data
 */
export async function buildF1PayloadFromFirebase(userId, inMemoryProfile = null, options = {}) {
  const { db } = await import("@/firebase");
  const { doc, getDoc, updateDoc } = await import("firebase/firestore");
  
  const docRef = doc(db, "users", userId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists() && !inMemoryProfile) {
    throw new Error("Profile not found");
  }
  
  const rootData = snapshot.exists() ? snapshot.data() : {};
  const rawFirestoreData = rootData.profile || rootData;
  const firestoreData = inMemoryProfile
    ? { ...rawFirestoreData, ...inMemoryProfile, resourceType: rawFirestoreData.resourceType || inMemoryProfile.resourceType }
    : rawFirestoreData;
  
  // If already in FHIR format, transform to backend format
  if (firestoreData.resourceType === "Patient") {
    const { translateFormKeys } = await import("@/data/loinc");
    
    // Transform FHIR format to backend legacy format
    const observations = firestoreData.observations || [];
    const conditions = firestoreData.conditions || [];
    
    // Extract name parts safely (prefer explicit nombres/apellidos if present)
    const rawName = (firestoreData.name || "").trim();
    const nameParts = rawName ? rawName.split(/\s+/) : [];
    const parsedNombres = nameParts.length > 1 ? nameParts.slice(0, -1).join(" ") : (nameParts[0] || "");
    const parsedApellidos = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
    const firstName = firestoreData.nombres || parsedNombres;
    const lastName = (firestoreData.apellidos !== undefined && firestoreData.apellidos !== "")
      ? firestoreData.apellidos
      : parsedApellidos;
    
    // Map gender with fallback to Spanish key `sexo`
    const gender = firestoreData.sexo
      ? firestoreData.sexo
      : firestoreData.gender === "male"
        ? "Masculino"
        : firestoreData.gender === "female"
          ? "Femenino"
          : "";
    const resolvedBirthDate = firestoreData.fechaNacimiento || firestoreData.birthDate || "";
    const resolvedEmail = firestoreData.email || rootData.email || "";
    const resolvedWhatsapp = firestoreData.whatsapp || rootData.whatsapp || "";

    // Self-heal canonical FHIR keys in Firestore if partial Spanish keys were written previously
    const canonicalFixes = {};
    if (!rawFirestoreData.birthDate && resolvedBirthDate) canonicalFixes["profile.birthDate"] = resolvedBirthDate;
    if ((!rawFirestoreData.gender || rawFirestoreData.gender === "other") && (gender === "Masculino" || gender === "Femenino")) {
      canonicalFixes["profile.gender"] = gender === "Masculino" ? "male" : "female";
    }
    if (!rawFirestoreData.email && resolvedEmail) canonicalFixes["profile.email"] = resolvedEmail;
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    if (fullName && rawFirestoreData.name !== fullName) canonicalFixes["profile.name"] = fullName;
    if (snapshot.exists() && Object.keys(canonicalFixes).length > 0) {
      Promise.resolve(updateDoc(docRef, canonicalFixes)).catch(() => {});
    }
    
    // Build vitalSigns array from observations; if observations is empty but flat Spanish fields exist, build from flat fields
    let vitalSigns = observations.map((obs) => ({
      code: obs.code,
      system: obs.system,
      display: obs.display,
      value: obs.value,
      ...(obs.valueCode && { valueCode: obs.valueCode }),
      ...(obs.unit && { unit: obs.unit }),
    }));
    if (vitalSigns.length === 0 && (firestoreData.peso || firestoreData.talla || firestoreData.paSistolica || firestoreData.heartRate)) {
      vitalSigns = buildSignosFromForm({
        peso: firestoreData.peso,
        talla: firestoreData.talla,
        cintura: firestoreData.cintura,
        paSistolica: firestoreData.paSistolica,
        paDiastolica: firestoreData.paDiastolica,
        glucemia: firestoreData.glucemia,
        oximetria: firestoreData.oximetria,
        tabaco: firestoreData.tabaco,
        alcohol: firestoreData.alcohol,
        sueno: firestoreData.sueno,
        heartRate: firestoreData.heartRate,
        heartRateVariability: firestoreData.heartRateVariability,
        respirationRate: firestoreData.respirationRate,
        stressIndex: firestoreData.stressIndex,
        cardiovascularAge: firestoreData.cardiovascularAge,
        cardiovascularBMI: firestoreData.cardiovascularBMI,
        cvdRisk: firestoreData.cvdRisk,
        wellnessScore: firestoreData.wellnessScore,
        ansIndexPNS: firestoreData.ansIndexPNS,
        ansIndexSNS: firestoreData.ansIndexSNS,
        hba1c: firestoreData.hba1c,
        hemoglobin: firestoreData.hemoglobin,
        cholesterol: firestoreData.cholesterol,
        shockIndex: firestoreData.shockIndex,
        prq: firestoreData.prq,
        hrvRMSSD: firestoreData.hrvRMSSD,
        hrvSDNN: firestoreData.hrvSDNN,
      });
    }
    
    // Build conditions array (backend format)
    let condicionesArray = conditions.map((cond) => ({
      name: cond.display,
      snomedCode: cond.code,
      icd10Code: cond.icd10Code || null, // May not exist in FHIR format
      source: cond.category || "manual",
    }));
    if (condicionesArray.length === 0 && ((firestoreData.condicionIds || []).length > 0 || (firestoreData.condicionesAdicionales || []).length > 0)) {
      const imc = calculateIMC(firestoreData.peso, firestoreData.talla);
      const autoIds = getAutoDetectedConditions({
        peso: firestoreData.peso,
        talla: firestoreData.talla,
        paSistolica: firestoreData.paSistolica,
        paDiastolica: firestoreData.paDiastolica,
        glucemia: firestoreData.glucemia,
      }, imc);
      condicionesArray = buildCondicionesArray(
        firestoreData.condicionIds || [],
        autoIds,
        firestoreData.condicionesAdicionales || []
      );
    }
    
    // Build medications array (backend format)
    const rawMeds = firestoreData.medications || firestoreData.medicamentos || [];
    const medicationsArray = rawMeds.map((med) => ({
      name: med.name || med.display || med.nombre || "",
      atcCode: med.atcCode || med.code || null,
      dosage: med.dosage || med.dosis || null,
      brandName: med.brandName || med.marca || null,
      source: med.source || "manual",
    }));
    
    const syncTimestamp = new Date().toISOString();
    const effectiveCompletedAt = options.forceTimestamp
      ? syncTimestamp
      : (firestoreData.completedAt || firestoreData.vitalsMeasurementDate || firestoreData.updatedAt || rootData.updatedAt || syncTimestamp);
    const effectiveCreatedAt = firestoreData.createdAt || rootData.createdAt || effectiveCompletedAt;

    // Build the legacy payload (Spanish keys for translateFormKeys)
    const legacyPayload = {
      email: resolvedEmail,
      whatsapp: resolvedWhatsapp,
      nombres: firstName,
      apellidos: lastName,
      fechaNacimiento: resolvedBirthDate,
      sexo: gender,
      estadoCivil: firestoreData.estadoCivil || firestoreData.maritalStatus || "",
      nivelEducativo: firestoreData.nivelEducativo || firestoreData.educationLevel || "",
      profesion: firestoreData.profesion || firestoreData.profession || "",
      ocupacion: firestoreData.ocupacion || firestoreData.occupation || "",
      residencia: firestoreData.residencia || firestoreData.residence || "",
      signos: vitalSigns,
      condiciones: condicionesArray,
      usaMedicamentos: firestoreData.usaMedicamentos || (medicationsArray.length > 0 ? "Si" : "No"),
      medicamentos: medicationsArray,
      consentimiento: firestoreData.consentimiento ?? firestoreData.consent ?? false,
      createdAt: effectiveCreatedAt,
      completedAt: effectiveCompletedAt,
    };
    
    // Translate to English keys that backend expects
    return translateFormKeys("F1", legacyPayload);
  }
  
  // Legacy format - transform as before
  const { buildSignosFromForm: buildSignosLegacy, buildCondicionesArray: buildCondsLegacy, translateFormKeys } = await import("@/data/loinc");
  const { getAutoDetectedConditions: getAutoLegacy, calculateIMC: calcImcLegacy } = await import("@/pages/FormF1/helpers");
  
  const signos = buildSignosLegacy({
    peso: firestoreData.peso,
    talla: firestoreData.talla,
    cintura: firestoreData.cintura,
    paSistolica: firestoreData.paSistolica,
    paDiastolica: firestoreData.paDiastolica,
    glucemia: firestoreData.glucemia,
    oximetria: firestoreData.oximetria,
    tabaco: firestoreData.tabaco,
    alcohol: firestoreData.alcohol,
    sueno: firestoreData.sueno,
    heartRate: firestoreData.heartRate,
    heartRateVariability: firestoreData.heartRateVariability,
    respirationRate: firestoreData.respirationRate,
    stressIndex: firestoreData.stressIndex,
    cardiovascularAge: firestoreData.cardiovascularAge,
    cardiovascularBMI: firestoreData.cardiovascularBMI,
    cvdRisk: firestoreData.cvdRisk,
    wellnessScore: firestoreData.wellnessScore,
    ansIndexPNS: firestoreData.ansIndexPNS,
    ansIndexSNS: firestoreData.ansIndexSNS,
    hba1c: firestoreData.hba1c,
    hemoglobin: firestoreData.hemoglobin,
    cholesterol: firestoreData.cholesterol,
    shockIndex: firestoreData.shockIndex,
    prq: firestoreData.prq,
    hrvRMSSD: firestoreData.hrvRMSSD,
    hrvSDNN: firestoreData.hrvSDNN,
  });

  const imc = calcImcLegacy(firestoreData.peso, firestoreData.talla);
  const autoIds = getAutoLegacy({
    peso: firestoreData.peso,
    talla: firestoreData.talla,
    paSistolica: firestoreData.paSistolica,
    paDiastolica: firestoreData.paDiastolica,
    glucemia: firestoreData.glucemia,
  }, imc);
  
  const condiciones = buildCondsLegacy(
    firestoreData.condicionIds || [],
    autoIds,
    firestoreData.condicionesAdicionales || []
  );

  const legacySyncTimestamp = new Date().toISOString();
  const legacyCompletedAt = options.forceTimestamp
    ? legacySyncTimestamp
    : (firestoreData.completedAt || firestoreData.vitalsMeasurementDate || firestoreData.updatedAt || rootData.updatedAt || legacySyncTimestamp);
  const legacyCreatedAt = firestoreData.createdAt || rootData.createdAt || legacyCompletedAt;

  return translateFormKeys("F1", {
    email: firestoreData.email || rootData.email || "",
    whatsapp: firestoreData.whatsapp || rootData.whatsapp || "",
    nombres: firestoreData.nombres,
    apellidos: firestoreData.apellidos,
    fechaNacimiento: firestoreData.fechaNacimiento,
    sexo: firestoreData.sexo,
    estadoCivil: firestoreData.estadoCivil,
    nivelEducativo: firestoreData.nivelEducativo,
    profesion: firestoreData.profesion,
    ocupacion: firestoreData.ocupacion,
    residencia: firestoreData.residencia,
    signos,
    condiciones,
    usaMedicamentos: firestoreData.usaMedicamentos,
    medicamentos: firestoreData.medicamentos,
    consentimiento: firestoreData.consentimiento,
    createdAt: legacyCreatedAt,
    completedAt: legacyCompletedAt,
  });
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Update Outbox state on users/{uid} in Firestore after a form sync attempt.
 */
async function recordFormOutboxStatus(formId, result, explicitUid = null) {
  try {
    const { auth, db } = await import("@/firebase");
    const { doc, setDoc } = await import("firebase/firestore");
    const userId = explicitUid || auth.currentUser?.uid;
    if (!userId) return;

    const pendingField = `pendingFhir${formId}`;
    const nowIso = new Date().toISOString();

    await setDoc(
      doc(db, "users", userId),
      result.ok
        ? {
            fhirReconciled: true,
            fhirSyncStatus: "SYNCED",
            [pendingField]: false,
            lastFhirSyncAt: nowIso,
            lastFhirSyncError: null,
          }
        : {
            fhirSyncStatus: "PENDING_FHIR",
            [pendingField]: true,
            lastFhirSyncAttemptAt: nowIso,
            lastFhirSyncError: result.error || `HTTP ${result.status}`,
          },
      { merge: true }
    );
  } catch (fsErr) {
    console.warn(`[sync${formId}] No se pudo actualizar estado Outbox en Firestore:`, fsErr.message);
  }
}

/**
 * Send a form payload to the backend with automatic retry on transient network/5xx errors.
 * Returns {ok, status, data} or {ok: false, error}.
 */
async function postForm(formId, userId, data, maxRetries = 2) {
  const payload = { form: formId, userId, data };
  const start = performance.now();

  console.group(`[WE360 GCP] postForm — ${formId}`);
  console.log("Proxy endpoint:", `${GCP_PROXY_URL}/forms`);
  console.log("Origin (browser):", window.location.origin);
  console.log("userId:", userId);

  let lastResult = { ok: false, status: 0, error: "Sync failed" };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      await sleep(500 * Math.pow(2, attempt - 1));
    }
    try {
      const res = await fetch(`${GCP_PROXY_URL}/forms`, {
        method: "POST",
        headers: getGcpHeaders(),
        body: JSON.stringify(payload),
      });
      const elapsed = Math.round(performance.now() - start);
      const body = await res.json().catch(() => null);
      console.log(`Attempt ${attempt + 1} response status:`, res.status, res.ok ? "✅ OK" : "❌ ERROR", `(${elapsed}ms)`);
      if (!res.ok) console.error("[WE360 GCP] Error body:", body);

      lastResult = { ok: res.ok, status: res.status, data: body };
      addSyncLogEntry({ form: formId, userId, method: "POST", endpoint: "/api/forms", status: res.status, ok: res.ok, time: elapsed, response: body });

      if (res.ok || (res.status >= 400 && res.status < 500)) {
        console.groupEnd();
        return lastResult;
      }
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      console.error(`[WE360 GCP] Attempt ${attempt + 1} network error:`, err.message);
      addSyncLogEntry({ form: formId, userId, method: "POST", endpoint: "/api/forms", status: 0, ok: false, time: elapsed, error: err.message });
      lastResult = { ok: false, error: err.message };
    }
  }

  console.groupEnd();
  return lastResult;
}

/**
 * Sync F1 form data to the backend (FHIR Source of Truth) with Outbox state tracking.
 * Reads fresh FHIR data from Firebase (merged with in-memory profile) and sends to GCP.
 * @param {Object} profile - The in-memory profile (used for latest fields and userId/email)
 * @param {string} email - The user's email (used as userId)
 */
export async function syncF1(profile, email) {
  try {
    const { auth } = await import("@/firebase");
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error("No authenticated user");
    }
    
    const userId = currentUser.uid;
    
    // Read fresh FHIR data directly from Firebase, merged with in-memory profile if provided
    const data = await buildF1PayloadFromFirebase(userId, profile);
    const result = await postForm("F1", email, data);
    await recordFormOutboxStatus("F1", result, userId);

    return result;
  } catch (error) {
    console.error("[syncF1] Error reading from Firebase:", error);
    return { ok: false, error: error.message };
  }
}

/**
 * Sync F2 form data to the backend with Outbox tracking.
 */
export async function syncF2(f2Results, email) {
  const data = translateFormKeys("F2", buildF2Firestore(f2Results));
  const result = await postForm("F2", email, data);
  await recordFormOutboxStatus("F2", result);
  return result;
}

/**
 * Sync F3 form data to the backend with Outbox tracking.
 */
export async function syncF3(f3Results, email) {
  const data = translateFormKeys("F3", buildF3Firestore(f3Results));
  const result = await postForm("F3", email, data);
  await recordFormOutboxStatus("F3", result);
  return result;
}

/**
 * Sync F4 form data to the backend with Outbox tracking.
 */
export async function syncF4(f4Results, email) {
  const data = translateFormKeys("F4", buildF4Firestore(f4Results));
  const result = await postForm("F4", email, data);
  await recordFormOutboxStatus("F4", result);
  return result;
}

/**
 * Sync F5 form data to the backend with Outbox tracking.
 */
export async function syncF5(f5Results, email) {
  const data = translateFormKeys("F5", buildF5Firestore(f5Results));
  const result = await postForm("F5", email, data);
  await recordFormOutboxStatus("F5", result);
  return result;
}


/**
 * Query the backend (GET endpoints) — for admin use.
 */
export async function queryBackend(endpoint, email) {
  const separator = endpoint.includes("?") ? "&" : "?";
  const proxiedEndpoint = encodeURIComponent(endpoint);
  const url = `${GCP_PROXY_URL}/query?endpoint=${proxiedEndpoint}&email=${encodeURIComponent(email)}`;
  const start = performance.now();
  try {
    const res = await fetch(url, { headers: { "Content-Type": "application/json" } });
    const elapsed = Math.round(performance.now() - start);
    const body = await res.json().catch(() => null);
    return { ok: res.ok, status: res.status, data: body, time: elapsed, url };
  } catch (err) {
    const elapsed = Math.round(performance.now() - start);
    return { ok: false, status: 0, error: err.message, time: elapsed, url };
  }
}

export { BACKEND_URL };

const FORM_QUESTIONNAIRE_MAP = {
  F2: { questionnaire: "F2-PAR-Q-Physical", legacyKey: "f2Results" },
  F3: { questionnaire: "F3-IPAQ", legacyKey: "f3Results" },
  F4: { questionnaire: "F4-Nutrition", legacyKey: "f4Results" },
  F5: { questionnaire: "F5-Mental-Health", legacyKey: "f5Results" },
};

/**
 * Admin resync: send pre-formatted Firestore data directly to GCP.
 * Used when data exists in Firebase but was never pushed to GCP, or when an Admin
 * explicitly forces local Firebase data to supersede newer/contaminated GCP observations.
 *
 * @param {string} formId - "F1" | "F2" | "F3" | "F4" | "F5"
 * @param {string} email - User email
 * @param {Object} formData - Local form data from Firestore
 * @param {string|null} uid - Firebase UID
 * @param {Object} options - { forceTimestamp?: boolean }
 */
export async function adminResyncForm(formId, email, formData, uid = null, options = {}) {
  const forceTimestamp = Boolean(options?.forceTimestamp);
  let result;
  if (formId === "F1" && uid) {
    const f1Payload = await buildF1PayloadFromFirebase(uid, null, { forceTimestamp });
    if (f1Payload) {
      result = await postForm("F1", email, f1Payload);
      await recordFormOutboxStatus("F1", result, uid);
      if (result.ok && forceTimestamp && f1Payload.completedAt) {
        try {
          const { db } = await import("@/firebase");
          const { doc, updateDoc } = await import("firebase/firestore");
          await updateDoc(doc(db, "users", uid), {
            "profile.completedAt": f1Payload.completedAt,
          });
        } catch (err) {
          console.warn("[adminResyncForm] No se pudo actualizar completedAt de F1 en Firestore:", err.message);
        }
        return { ...result, forcedCompletedAt: f1Payload.completedAt };
      }
      return result;
    }
  }
  const builders = {
    F2: buildF2Firestore,
    F3: buildF3Firestore,
    F4: buildF4Firestore,
    F5: buildF5Firestore,
  };
  let payload = formData;
  if (builders[formId] && formData && typeof formData === "object") {
    const isAlreadyBuilt =
      (formId === "F2" && Boolean(formData.observaciones || formData.observations)) ||
      (formId === "F3" && Boolean(formData.observaciones || formData.observations)) ||
      (formId === "F4" && Boolean(formData.consumo?.[0]?.code || formData.foodIntake?.[0]?.code)) ||
      (formId === "F5" && Boolean(typeof formData.ansiedad?.respuestas?.[0] === "object" || typeof formData.anxiety?.responses?.[0] === "object"));
    const firestoreObj = isAlreadyBuilt ? formData : builders[formId](formData);
    payload = translateFormKeys(formId, firestoreObj);
  }
  const forcedCompletedAt = forceTimestamp ? new Date().toISOString() : null;
  if (payload && typeof payload === "object") {
    const preservedCompletedAt =
      payload.completedAt ||
      formData?.completedAt ||
      formData?.createdAt ||
      formData?.date ||
      new Date().toISOString();
    payload = {
      ...payload,
      completedAt: forcedCompletedAt || preservedCompletedAt,
    };
  }
  result = await postForm(formId, email, payload);
  if (uid) {
    await recordFormOutboxStatus(formId, result, uid);
    if (result.ok && forcedCompletedAt && FORM_QUESTIONNAIRE_MAP[formId]) {
      try {
        const { db } = await import("@/firebase");
        const { doc, getDoc, updateDoc } = await import("firebase/firestore");
        const { questionnaire, legacyKey } = FORM_QUESTIONNAIRE_MAP[formId];
        const userRef = doc(db, "users", uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const rootData = snap.data() || {};
          const prof = rootData.profile || {};
          const updates = {};
          if (Array.isArray(prof.other)) {
            updates["profile.other"] = prof.other.map((entry) =>
              entry?.questionnaire === questionnaire && entry?.data
                ? { ...entry, data: { ...entry.data, completedAt: forcedCompletedAt } }
                : entry
            );
          }
          if (prof[legacyKey] && typeof prof[legacyKey] === "object") {
            updates[`profile.${legacyKey}.completedAt`] = forcedCompletedAt;
          }
          if (Object.keys(updates).length > 0) {
            await updateDoc(userRef, updates);
          }
        }
      } catch (err) {
        console.warn(`[adminResyncForm] No se pudo actualizar completedAt de ${formId} en Firestore:`, err.message);
      }
      return { ...result, forcedCompletedAt };
    }
  }
  return result;
}


